import PropTypes from "prop-types";
import { memo, useMemo, useTransition } from "react";

// @mui material components
import Icon from "@mui/material/Icon";
import { Card, Divider } from "@mui/material";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftBadge from "components/SoftComponents/SoftBadge";
import SoftButton from "components/SoftComponents/SoftButton";

// Custom styles for the NewTimelineItem
import { timelineItem, timelineItemIcon } from "modules/inspections/components/InspectionTimeline/NewTimelineItem/styles";
import { useSoftUIController } from "hooks/context";
import { calculateOverdue, calculateTimeDiff, determineReportingDate } from "utils/inspection";

function NewTimelineItem({
  itemData,
  itemData: { inspectionData, priority, icon, title, secondary_title = "", devices_desc, maintenance_tag, recordType },
  isLeft = false,
  todayItem,
  firstItem,
  lastItems,
  selectedInspectionId,
  setSelectedInspection
}) {
  const [controller] = useSoftUIController();
  const { appearance, selectedOrg } = controller;

  // console.log(inspectionData.devices)

  const [isPending, startTransition] = useTransition();

  // 1. Calculate booleans ONCE
  const hasSelected = selectedInspectionId != null;
  const isThisSelected = selectedInspectionId === inspectionData._id;

  // 2. Formatting & Date Logic
  const { label, reported_date, timer_date, tag, overdue } = determineReportingDate(itemData, selectedOrg);

  const overdueStyling = overdue ? calculateOverdue(timer_date, selectedOrg, true) : "0, 0, 0, 0.3";
  const rgbBase = overdueStyling.split(',').slice(0, 3).join(',');

  let priority_colour = "secondary";
  if (priority === "medium") priority_colour = "warning";
  else if (priority === "high") priority_colour = "error";

  // 3. Memoize the heavy dynamic styles to prevent CSS-in-JS recalculations!
  const cardStyle = useMemo(() => ({
    borderRadius: '6px',
    border: '1px solid #e9e9e9',
    boxShadow: isThisSelected || overdueStyling !== "0, 0, 0, 0.3"
      ? `0 0 15px 2px rgba(${overdueStyling})`
      : `0 0 15px 2px rgba(${rgbBase}, 0)`,
    transition: "box-shadow 300ms ease-in-out",
    cursor: 'pointer',
    "&:hover": {
      boxShadow: `0 0 15px 2px rgba(${overdueStyling}) !important`
    }
  }), [isThisSelected, overdueStyling, rgbBase]);

  const titleTypographyStyle = useMemo(() => ({ breakpoints, typography }) => ({
    ...typography.h6,
    fontWeight: "bold",
    lineHeight: '1.4',
    textTransform: 'capitalize',
    [breakpoints.up('sm')]: { ...typography.h5, fontWeight: "bold", lineHeight: '1.4', textTransform: 'capitalize' },
    [hasSelected && breakpoints.up('xl')]: { ...typography.h6, fontWeight: "bold", lineHeight: '1.4', textTransform: 'capitalize' },
    [breakpoints.up('x4l')]: { ...typography.h5, fontWeight: "bold", lineHeight: '1.4', textTransform: 'capitalize' }
  }), [hasSelected]);

  return (
    <SoftBox
      position="relative"
      width="100%"
      justifyContent={{ xxs: "flex-start", md: isLeft ? "flex-start" : "flex-end", xl: "flex-start", x4l: isLeft ? "flex-start" : "flex-end" }}
      sx={(theme) => timelineItem(theme, { isLeft, firstItem, lastItems, todayItem })}
    >
      <SoftBox
        width="1.625rem"
        height="1.625rem"
        borderRadius="50%"
        position="absolute"
        top={firstItem ? "1.25rem" : "calc(1.25rem + 10px)"}
        zIndex={2}
        sx={({ palette: { gradients }, functions: { linearGradient } }) => ({
          background: linearGradient(gradients[icon.color].main, gradients[icon.color].state),
          left: { xxs: '0px', sm: '6px' },
          transform: "none",
        })}
      >
        <Icon sx={(theme) => timelineItemIcon(theme, { color: "white", hasSelected })}>{icon.icon}</Icon>
      </SoftBox>

      <SoftBox
        display="flex"
        justifyContent={isLeft ? "flex-start" : "flex-end"}
        width="100%"
        pl={{ xxs: 4, sm: 6 }}
        pr={0}
      >
        <div style={{ width: '100%' }}>
          <Card sx={cardStyle} onClick={() => {
            startTransition(() => setSelectedInspection(inspectionData));
          }}>
            <div style={{ padding: '16px' }}>

              {/* Top Section */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                <SoftBox display="flex" flexDirection={'row'} alignItems={'center'} gap={{ xxs: 0, sm: 1.5, xl: !hasSelected ? 1.5 : 0, x4l: 1.5 }}>
                  <SoftTypography color={appearance} sx={titleTypographyStyle}>
                    {!secondary_title.trim().length ? title : secondary_title}
                  </SoftTypography>
                  <SoftBox display={{ xxs: "none", sm: "flex", xl: !hasSelected ? "flex" : "none", x4l: "flex" }}>
                    <SoftBadge
                      sx={{ '.MuiBadge-badge': { borderRadius: '0.9rem !important', fontSize: '11px !important' } }}
                      variant="contained"
                      color={tag.color}
                      badgeContent={tag.label}
                      container
                    />
                  </SoftBox>
                </SoftBox>

                <div style={{ display: 'flex', gap: '4px' }}>
                  {maintenance_tag && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4px 8px', border: '1px solid #eee', backgroundColor: maintenance_tag.colour, borderRadius: '8px' }}>
                      <Icon fontSize="14px !important" sx={{ fontSize: "14px", color: "#fff" }}>beenhere</Icon>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', border: '1px solid #eee', borderRadius: '8px' }}>
                    <Icon fontSize="20px !important" sx={{ fontSize: "20px", color: "#8392ab" }}>photo</Icon>
                    <SoftTypography variant="button" fontWeight="medium" color="text">{inspectionData.images?.[0]?.paths?.length || 0}</SoftTypography>
                  </div>
                </div>
              </div>

              {/* Middle Section - Left SoftBox because it relies heavily on responsive breakpoints */}
              <SoftBox display="flex" flexDirection={{ xxs: "column", sm: "row", xl: !hasSelected ? "row" : "column", x4l: "row" }} mt={2.5} flex={1} gap={{ xxs: 0.5, sm: 1.5, xl: !hasSelected ? 1.5 : 0.5, x4l: 1.5 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 4 }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Icon fontSize="16px !important" sx={{ paddingLeft: '2px', fontSize: "16px", color: "#8392ab" }}>calendar_today</Icon>
                    <SoftBox display={{ xxs: "flex" }} pt={0.25}>
                      <SoftTypography pl={0.25} variant="caption" fontWeight="medium" color="text">
                        <SoftTypography variant="caption" fontWeight="regular" color="secondary">
                          {label}
                        </SoftTypography>
                        {calculateTimeDiff(reported_date)}
                      </SoftTypography>
                    </SoftBox>
                  </div>
                </div>

                <Divider orientation="vertical" flexItem />

                <div style={{ display: 'flex', flex: 4 }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Icon fontSize="small" sx={{ color: "#8392ab" }}>location_on</Icon>
                    {selectedOrg == 'clearways' ? (
                      <SoftBox display='flex' flexDirection='column'>
                        <SoftTypography pt={0.25} variant="caption" fontWeight="medium" color="text">
                          {devices_desc}
                        </SoftTypography>
                        <SoftTypography pt={0.25} variant="caption" fontWeight="medium" color="text">
                          {inspectionData.devices[0].stop_id}
                        </SoftTypography>
                      </SoftBox>
                    ) : (
                      <SoftTypography pt={0.25} variant="caption" fontWeight="medium" color="text">
                        {devices_desc}
                      </SoftTypography>
                    )}
                  </div>
                </div>

                <Divider orientation="vertical" flexItem />

                <SoftBox display={{ xxs: "none", sm: "flex" }} flex={{ xxs: 4 }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Icon fontSize="small" sx={{ color: "#8392ab" }}>person</Icon>
                    <SoftBox display="flex" flexDirection={{ xxs: "row", sm: "column", xl: !hasSelected ? "column" : "row", x4l: 'column' }} pt={0.25} gap={{ xxs: 0.25, xl: !hasSelected ? 0.25 : 0.5 }}>
                      <SoftTypography variant="caption" textTransform="capitalize" fontWeight="regular" color="secondary">Inspector</SoftTypography>
                      <SoftTypography variant="caption" textTransform="capitalize" fontWeight="medium" color="text">{inspectionData.technician}</SoftTypography>
                    </SoftBox>
                  </div>
                </SoftBox>
              </SoftBox>

              <SoftBox pt={{ xxs: 1.5, sm: 2.5, xl: !hasSelected ? 2.5 : 1.5, x4l: 2.5 }} pb={1.5}>
                <Divider orientation="horizontal" />
              </SoftBox>

              {/* Lower Section */}
              <SoftBox display="flex" flexDirection={{ xxs: "column", sm: 'row', xl: !hasSelected ? "row" : "column", x4l: 'row' }} gap={{ xxs: 1.5, sm: 0, xl: !hasSelected ? 0 : 1.5, x4l: 0 }} justifyContent={{ xxs: "flex-start", sm: "space-between", xl: !hasSelected ? "space-between" : "flex-start", x4l: "space-between" }}>

                <SoftBox display="inline-flex" gap={0.5}>

                  <SoftBox display="flex" flex={1} flexDirection="row" gap={0.5}>
                    <SoftBox display={{ xxs: "flex", sm: "none", xl: !hasSelected ? "none" : "flex", x4l: "none" }} flex={{ xxs: 6, sm: 0, xl: !hasSelected ? 0 : 6, x4l: 0 }} width='100%'>
                      <SoftBadge
                        sx={{ width: "100%", minWidth: '100%', '.MuiBadge-badge': { width: "100%", minWidth: '100%', borderRadius: '0.9rem !important', fontSize: '11px !important' } }}
                        variant="contained" color={tag.color} badgeContent={tag.label} container
                      />
                    </SoftBox>

                    <SoftBox display="flex" flex={{ xxs: 6, sm: 12, xl: !hasSelected ? 12 : 6, x4l: 12 }} justifyContent={{ xxs: "center", sm: 'flex-start', xl: !hasSelected ? "flex-start" : "center", x4l: 'flex-start' }} alignItems="center" gap={1}>
                      <Icon fontSize="20px !important" sx={({ palette: { gradients }, functions: { linearGradient } }) => ({
                        fontSize: "20px", background: linearGradient(gradients[priority_colour].main, gradients[priority_colour].state),
                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                      })}>flag</Icon>
                      <SoftTypography variant="caption" pt={0.25} textTransform="capitalize" fontWeight="medium" color="text">{priority || "Low"} Priority</SoftTypography>
                    </SoftBox>
                  </SoftBox>

                  {recordType == 'workshop' && (
                    <SoftBox display={{ xxs: "flex", sm: "none", xl: hasSelected ? "flex" : "none", x4l: "none" }} justifyContent="center" alignItems="center">
                      <Icon fontSize="medium">build_circle</Icon>
                    </SoftBox>
                  )}
                </SoftBox>

                <SoftBox display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                  {recordType == 'workshop' && (
                    <SoftBox display={{ xxs: "none", sm: "flex", xl: !hasSelected ? "flex" : "none", x4l: "flex" }}>
                      <Icon fontSize="medium">build_circle</Icon>
                    </SoftBox>
                  )}
                  <SoftButton color="light" sx={({ breakpoints }) => ({
                    width: '100%',
                    fontSize: '12px !important', padding: "0.25rem 0.75rem !important", minHeight: '2rem !important', gap: 1,
                    [breakpoints.up('sm')]: { fontSize: '14px !important' },
                    [breakpoints.up('xl')]: { fontSize: !hasSelected ? '14px !important' : '12px !important' },
                    [breakpoints.up('x4l')]: { fontSize: '14px !important' },
                  })}>
                    <SoftTypography sx={{ fontSize: 'inherit' }} variant="button" color="dark" fontWeight="medium">View Details</SoftTypography>
                    <Icon>arrow_forward</Icon>
                  </SoftButton>
                </SoftBox>
              </SoftBox>

            </div>
          </Card>
        </div>
      </SoftBox>
    </SoftBox>
  );
}

const areEqual = (prevProps, nextProps) => {
  // 1. Text & Basic Content
  if (prevProps.title !== nextProps.title) return false;
  if (prevProps.status !== nextProps.status) return false;
  if (prevProps.itemData?.devices_desc !== nextProps.itemData?.devices_desc) return false;
  if (prevProps.itemData?.technician !== nextProps.itemData?.technician) return false;

  // 2. Defeat Inline Object trap
  if (
    prevProps.lastItems?.day !== nextProps.lastItems?.day ||
    prevProps.lastItems?.month !== nextProps.lastItems?.month ||
    prevProps.lastItems?.page !== nextProps.lastItems?.page
  ) return false;

  // 3. Layout Flags
  if (prevProps.todayItem !== nextProps.todayItem) return false;
  if (prevProps.firstItem !== nextProps.firstItem) return false;
  if (prevProps.isLeft !== nextProps.isLeft) return false;

  // 4. Force layout recalculation if the side panel opens/closes 
  // (because your responsive breakpoints rely on `hasSelected` to hide/show UI elements)
  const prevHasSelected = prevProps.selectedInspectionId != null;
  const nextHasSelected = nextProps.selectedInspectionId != null;
  if (prevHasSelected !== nextHasSelected) return false;

  // 5. Highlight/Selection state (USE ID INSTEAD OF OBJECT!)
  const prevIsSelected = prevProps.selectedInspectionId === prevProps.itemData?.inspectionData?._id;
  const nextIsSelected = nextProps.selectedInspectionId === nextProps.itemData?.inspectionData?._id;

  if (prevIsSelected !== nextIsSelected) return false;

  return true;
};

NewTimelineItem.propTypes = {
  inspectionData: PropTypes.object,
  itemData: PropTypes.object,
  isLeft: PropTypes.bool,
  lastItems: PropTypes.object,
  selectedInspectionId: PropTypes.string,
  setSelectedInspection: PropTypes.func,
};

export default memo(NewTimelineItem, areEqual);